import { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../../../common/Loader";
import RolesService from "../../../../../services/AdminManagment/RolesService";
import "../style.css";

// ─── helpers ──────────────────────────────────────────────
const buildInitialPerms = (moduleList = [], actionList = []) => {
	const perms = {};
	moduleList.forEach((m) => {
		perms[m] = {};
		actionList.forEach((a) => {
			perms[m][a] = false;
		});
	});
	return perms;
};

const formatModuleName = (str) =>
	str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const getInitial = (str) => str.replace(/_/g, " ").trim()[0].toUpperCase();

// ─── main component ───────────────────────────────────────
const AddPermission = () => {
	const [roleName, setRoleName] = useState("");
	const [loading, setLoading] = useState(false);
	const [modules, setModules] = useState([]);
	const [actions, setActions] = useState([]);
	const [permissions, setPermissions] = useState({});

	const navigate = useNavigate();
	const location = useLocation();
	const roleService = new RolesService();
	const Auth = useSelector((state) => state.auth?.auth);
	const lang = useSelector((state) => state?.auth.lang);
	const isExist = (data) => Auth?.roles?.includes(data);
	const id = location?.state?.id;

	// ─── fetch modules + optional existing role ──────────
	useEffect(() => {
		setLoading(true);
		const requests = [roleService.getModule()];
		if (id) requests.push(roleService.find(id));

		Promise.all(requests).then((res) => {
			setLoading(false);

			if (!res[0]?.status) return;

			const { modules: mods, actions: acts } = res[0].data.data;
			setModules(mods);
			setActions(acts);

			// build fresh permissions grid from API data
			const freshPerms = buildInitialPerms(mods, acts);

			// if editing, overlay saved permissions
			if (id && res[1]?.status) {
				const resFind = res[1].data.data; // { id, name, permissions: [...] }
				setRoleName(resFind?.name ?? "");

				resFind?.permissions?.forEach(({ module, actions: savedActions }) => {
					if (!freshPerms[module]) return;
					savedActions.forEach((action) => {
						if (freshPerms[module][action] !== undefined) {
							freshPerms[module][action] = true; // check only what the API returned
						}
					});
				});
			}

			setPermissions(freshPerms);
		});
	}, []);

	// ─── derived count ────────────────────────────────────
	const totalSelected =
		modules.length && actions.length
			? modules.reduce(
					(acc, m) => acc + actions.filter((a) => permissions[m]?.[a]).length,
					0
			  )
			: 0;

	// ─── handlers ────────────────────────────────────────
	const handleToggle = (module, action, checked) => {
		setPermissions((prev) => ({
			...prev,
			[module]: { ...prev[module], [action]: checked },
		}));
	};

	const handleReset = () => {
		setRoleName("");
		setPermissions(buildInitialPerms(modules, actions));
	};

	const onSubmit = () => {
		const payload = {
			name: roleName,
			permissions: Object.entries(permissions).reduce(
				(acc, [module, actionsMap]) => {
					const checkedActions = Object.entries(actionsMap)
						.filter(([, checked]) => checked)
						.map(([action]) => action);
					if (checkedActions.length > 0) {
						acc.push({ module, actions: checkedActions });
					}
					return acc;
				},
				[]
			),
		};

		if (id) {
			roleService.update(id, payload).then((res) => {
				if (res?.status === 200) {
					toast.success(res?.data?.message);
					navigate("/roles");
				}
			});
		} else {
			roleService.create(payload).then((res) => {
				if (res?.status === 201) {
					toast.success(res?.data?.message);
					navigate("/roles");
				}
			});
		}
	};

	// ─── render ───────────────────────────────────────────
	if (loading) {
		return (
			<Card style={{ height: "300px" }}>
				<Card.Body>
					<Loader />
				</Card.Body>
			</Card>
		);
	}

	return (
		<div className="rp-page">
			{/* Section 01 */}
			<div className="rp-section">
				<div className="rp-section-header">
					<span className="rp-step-badge">01</span>
					<span className="rp-section-title">Role Information</span>
				</div>
				<div className="rp-role-inputs">
					<input
						className="rp-input"
						type="text"
						required
						placeholder="e.g. Manager"
						value={roleName}
						onChange={(e) => setRoleName(e.target.value)}
					/>
				</div>
			</div>

			{/* Section 02 */}
			<div className="rp-section">
				<div className="rp-section-header">
					<span className="rp-step-badge">02</span>
					<span className="rp-section-title">Module Permissions</span>
				</div>
				<div className="rp-modules-grid">
					{modules.map((mod) => (
						<ModuleCard
							key={mod}
							module={mod}
							permissions={permissions[mod] ?? {}}
							onToggle={handleToggle}
							actions={actions}
						/>
					))}
				</div>
			</div>

			{/* Footer */}
			<div className="rp-footer">
				<span className="rp-actions-count">
					<strong>{totalSelected}</strong>{" "}
					{totalSelected === 1 ? "Action" : "Actions"} Selected
				</span>
				<div className="rp-footer-btns">
					<button className="rp-btn-reset" onClick={handleReset}>
						Reset
					</button>
					<button className="btn btn-primary" onClick={onSubmit}>
						{id ? "Update Role" : "Create Role"}
					</button>
				</div>
			</div>
		</div>
	);
};

export default AddPermission;

// ─── ModuleCard ───────────────────────────────────────────
function ModuleCard({ module, permissions, onToggle, actions }) {
	return (
		<div className="rp-mod-card">
			<div className="rp-mod-header">
				<div className="rp-mod-initial">{getInitial(module)}</div>
				<span className="rp-mod-name">{formatModuleName(module)}</span>
			</div>
			<div className="rp-mod-actions">
				{actions.map((action) => (
					<label key={action} className="rp-cb-item">
						<input
							type="checkbox"
							checked={permissions?.[action] ?? false}
							onChange={(e) => onToggle(module, action, e.target.checked)}
						/>
						<span>{action.charAt(0) + action.slice(1).toLowerCase()}</span>
					</label>
				))}
			</div>
		</div>
	);
}
